import { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { useUser } from "@clerk/react";
import { setSentryUser, clearSentryUser } from "@/lib/sentry";
import { identifyUser, resetAnalytics, track, trackPageView } from "@/lib/analytics";

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
    trackPageView();
  }, [pathname]);
  return null;
}
import { LandingPageV2 } from "@/pages/LandingPageV2";
import { PricingPage } from "@/pages/PricingPage";
import { FeaturesPage } from "@/pages/FeaturesPage";
import { TemplatesPage } from "@/pages/TemplatesPage";
import { IntegrationsPage } from "@/pages/IntegrationsPage";
import { ChangelogPage } from "@/pages/ChangelogPage";
import { BlogPage } from "@/pages/BlogPage";
import { BlogPostPage } from "@/pages/BlogPostPage";
import { TermsPage } from "@/pages/TermsPage";
import { PrivacyPage } from "@/pages/PrivacyPage";
import { CookiePage } from "@/pages/CookiePage";
import { LoginPage } from "@/pages/LoginPage";
import { SignUpPage } from "@/pages/SignUpPage";
import { WaitlistPage } from "@/pages/WaitlistPage";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import { DashboardPage } from "@/pages/DashboardPage";
import { ProjectsPage } from "@/pages/ProjectsPage";
import { AssetsPage } from "@/pages/AssetsPage";
import { StarredPage } from "@/pages/StarredPage";
import { ProjectPage } from "@/pages/ProjectPage";
import { SettingsPage } from "@/pages/SettingsPage";
import { SupportPage } from "@/pages/SupportPage";
import { OnboardingPage } from "@/pages/OnboardingPage";
import { SsoCallbackPage } from "@/pages/SsoCallbackPage";
import { NotificationProvider } from "@/components/ui/notification";

function UserSync() {
  const { isSignedIn, user } = useUser();
  useEffect(() => {
    if (isSignedIn && user) {
      setSentryUser({ id: user.id, email: user.primaryEmailAddress?.emailAddress });
      identifyUser(user.id, { email: user.primaryEmailAddress?.emailAddress, name: user.fullName ?? undefined });
      track("Sign In", { user_id: user.id, login_method: user.primaryEmailAddress ? "email" : "oauth", success: true });
    } else if (isSignedIn === false) {
      clearSentryUser();
      resetAnalytics();
    }
  }, [isSignedIn, user]);
  return null;
}

export function App() {
  return (
    <BrowserRouter>
      <ScrollToTop />
      <UserSync />
      <NotificationProvider />
      <Routes>
        <Route path="/" element={<LandingPageV2 />} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/features" element={<FeaturesPage />} />
        <Route path="/templates" element={<TemplatesPage />} />
        <Route path="/integrations" element={<IntegrationsPage />} />
        <Route path="/changelog" element={<ChangelogPage />} />
        <Route path="/blog" element={<BlogPage />} />
        <Route path="/blog/:slug" element={<BlogPostPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/cookies" element={<CookiePage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/signup" element={<WaitlistPage />} />
        <Route path="/signup-full" element={<SignUpPage />} />
        <Route
          path="/onboarding"
          element={
            <ProtectedRoute>
              <OnboardingPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <DashboardPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard/projects"
          element={
            <ProtectedRoute>
              <ProjectsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard/assets"
          element={
            <ProtectedRoute>
              <AssetsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard/starred"
          element={
            <ProtectedRoute>
              <StarredPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/projects/:projectId"
          element={
            <ProtectedRoute>
              <ProjectPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings"
          element={
            <ProtectedRoute>
              <SettingsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/projects/:projectId/settings"
          element={
            <ProtectedRoute>
              <SettingsPage />
            </ProtectedRoute>
          }
        />
        <Route path="/support" element={<SupportPage />} />
        <Route path="/sso-callback" element={<SsoCallbackPage />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
